import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, Plus, Edit2, Trash2, X, Check, Loader2, 
  Pill, Scissors, BedDouble, Stethoscope, HeartPulse, 
  BriefcaseMedical, Syringe, Layers, ShieldCheck, PlusCircle, MinusCircle
} from 'lucide-react';

const AVAILABLE_ICONS = {
  Pill: Pill,
  Scissors: Scissors,
  BedDouble: BedDouble,
  Stethoscope: Stethoscope,
  HeartPulse: HeartPulse,
  BriefcaseMedical: BriefcaseMedical,
  Syringe: Syringe,
  Layers: Layers,
  ShieldCheck: ShieldCheck,
  Settings: Settings
};

export default function ManageServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState(null); // null means adding a new service
  
  // Form fields
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('Settings');
  const [desc, setDesc] = useState('');
  const [setup, setSetup] = useState('');
  const [points, setPoints] = useState(['']);
  const [image, setImage] = useState('');
  
  // No error state

  const fetchServices = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'services'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServices(list);
    } catch (err) {
      console.error("Error loading services:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load services from Firestore.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const openAddModal = () => {
    setCurrentService(null);
    setTitle('');
    setIcon('Settings');
    setDesc('');
    setSetup('');
    setPoints(['']);
    setImage('');
    setModalOpen(true);
  };

  const openEditModal = (service) => {
    setCurrentService(service);
    setTitle(service.title || '');
    setIcon(service.icon || 'Settings');
    setDesc(service.desc || '');
    setSetup(service.setup || '');
    setPoints(service.points && service.points.length > 0 ? [...service.points] : ['']);
    setImage(service.image || '');
    setModalOpen(true);
  };

  const handleAddPoint = () => {
    setPoints([...points, '']);
  };

  const handleRemovePoint = (index) => {
    if (points.length === 1) return;
    const updated = [...points];
    updated.splice(index, 1);
    setPoints(updated);
  };

  const handlePointChange = (index, val) => {
    const updated = [...points];
    updated[index] = val;
    setPoints(updated);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size limit: 800 KB = 819,200 bytes
    if (file.size > 819200) {
      Swal.fire({
        title: 'Image Too Large',
        text: 'The uploaded image exceeds the 800 KB limit. Please upload a smaller image.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      e.target.value = null; // Reset file input
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.onerror = (err) => {
      console.error('Image reading error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to process image file.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validations
    if (!title || !desc || !setup || !image) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Service Title, Description, Primary Installations, and Service Image are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Check for duplicate title
    const isDuplicate = services.some(s => 
      s.title.trim().toLowerCase() === title.trim().toLowerCase() && 
      (!currentService || s.id !== currentService.id)
    );
    if (isDuplicate) {
      Swal.fire({
        title: 'Duplicate Title',
        text: 'A service with this title already exists.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const filteredPoints = points.map(p => p.trim()).filter(p => p !== '');
    if (filteredPoints.length === 0) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Please add at least one highlight/inclusion point.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const serviceData = {
      title: title.trim(),
      icon,
      desc: desc.trim(),
      setup: setup.trim(),
      points: filteredPoints,
      image,
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentService) {
        // Update operation
        const docRef = doc(db, 'services', currentService.id);
        await updateDoc(docRef, serviceData);
        Swal.fire({
          title: 'Success',
          text: 'Service successfully updated!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        // Create operation
        serviceData.createdAt = new Date();
        await addDoc(collection(db, 'services'), serviceData);
        Swal.fire({
          title: 'Success',
          text: 'Service successfully created!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }
      
      // Reload and close modal
      await fetchServices();
      setTimeout(() => {
        setModalOpen(false);
      }, 1000);
    } catch (err) {
      console.error("Error saving service:", err);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving the service.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'services', id));
      setServices(services.filter(s => s.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Service successfully deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting service:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete service.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Manage Services</h1>
          <p className="text-xs text-gray-500">Add, edit, or delete specialized healthcare solutions and distribution services.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="btn-primary py-2.5 px-5 text-sm shrink-0 self-start sm:self-center"
        >
          <Plus size={16} />
          Create Service
        </button>
      </div>



      {/* Services Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
          <Loader2 className="animate-spin text-brand-blue" size={36} />
          <p className="text-xs font-bold text-gray-500">Loading healthcare services...</p>
        </div>
      ) : services.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
          <Settings size={48} className="mx-auto text-gray-300 animate-spin-slow" />
          <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Services Found</h3>
          <p className="text-xs font-light text-gray-500">Your services catalog is empty. Click "Create Service" to populate initial offerings.</p>
          <button onClick={openAddModal} className="btn-primary py-2 px-4 text-xs mx-auto">Create Service</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((svc) => {
            const Icon = AVAILABLE_ICONS[svc.icon] || Settings;
            return (
              <motion.div
                key={svc.id}
                layout
                className="bg-white p-6 rounded-2xl border border-brand-silver/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-all group"
              >
                <div className="space-y-4">
                  {/* Top Header & Icon Row */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand-light text-brand-blue flex items-center justify-center border border-brand-silver/40">
                        <Icon size={20} />
                      </div>
                      <div>
                        <h3 className="text-base font-bold font-montserrat text-brand-navy mt-0.5">{svc.title}</h3>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => openEditModal(svc)}
                        className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-brand-blue hover:bg-brand-blue/5 transition-colors"
                        title="Edit Service"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        onClick={() => handleDelete(svc.id)}
                        className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete Service"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Image banner display if exists */}
                  {svc.image && (
                    <div className="w-full h-36 rounded-xl overflow-hidden border border-brand-silver/50 shadow-inner">
                      <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                    {svc.desc}
                  </p>

                  {/* Setup & Bullet Points Count */}
                  <div className="pt-2 border-t border-brand-silver/50 flex items-center justify-between text-[10px] text-gray-400 font-semibold">
                    <span>Inclusions: <strong className="text-brand-navy">{svc.points?.length || 0} bullets</strong></span>
                    <span>Installation: <strong className="text-brand-blue">{svc.setup}</strong></span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* CRUD Overlay Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            {/* Modal Body */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentService ? 'Edit Healthcare Service' : 'Create Healthcare Service'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide configurations for public directory integration.</p>
                </div>
                <button 
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-6 space-y-6">

                {/* Form fields */}
                <div className="space-y-4">
                  {/* Service Title */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Service Title *</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Modular OT Setup"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Icon Selector */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Lucide Icon *</label>
                    <select
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
                    >
                      {Object.keys(AVAILABLE_ICONS).map((iconName) => (
                        <option key={iconName} value={iconName}>{iconName}</option>
                      ))}
                    </select>
                  </div>

                  {/* Primary Installation Environments */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Primary Installations *</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. ICU Wards, Emergency Rooms"
                      value={setup}
                      onChange={(e) => setSetup(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Service Description *</label>
                  <textarea 
                    rows={4}
                    required
                    placeholder="Enter service details, target outcomes, compliance markers, etc."
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Service Image Upload Option */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Service Image * (Up to 800 KB)</label>
                  {!image ? (
                    <div className="relative border-2 border-dashed border-brand-silver hover:border-brand-blue rounded-xl p-6 flex flex-col items-center justify-center bg-brand-light/35 transition-colors cursor-pointer">
                      <input 
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="text-center space-y-1.5">
                        <PlusCircle className="mx-auto text-gray-400" size={24} />
                        <p className="text-xs text-gray-500 font-poppins font-semibold text-brand-blue">Click to upload image</p>
                        <p className="text-[10px] text-gray-400 font-poppins">PNG, JPG or WEBP (Max 800 KB)</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 rounded-xl border border-brand-blue/20 bg-brand-blue/5 shadow-inner">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-brand-silver shrink-0 bg-white">
                          <img src={image} alt="Service preview" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-brand-navy font-poppins">Image uploaded successfully</p>
                          <p className="text-[10px] text-gray-500 font-poppins">Size is under 800 KB limit</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold font-poppins transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Points / Highlights Array */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Highlights & Inclusions *</label>
                    <button 
                      type="button" 
                      onClick={handleAddPoint}
                      className="text-xs text-brand-blue hover:text-brand-navy flex items-center gap-1 font-bold"
                    >
                      <PlusCircle size={14} /> Add Highlight
                    </button>
                  </div>

                  <div className="space-y-2">
                    {points.map((pt, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <span className="text-xs font-bold text-gray-400 shrink-0 w-4">{idx + 1}.</span>
                        <input 
                          type="text"
                          required
                          placeholder="e.g. Sourced directly from global manufacturers."
                          value={pt}
                          onChange={(e) => handlePointChange(idx, e.target.value)}
                          className="flex-grow px-4 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                        />
                        <button 
                          type="button"
                          disabled={points.length === 1}
                          onClick={() => handleRemovePoint(idx)}
                          className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                        >
                          <MinusCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={saving}
                    onClick={() => setModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving configurations...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentService ? 'Save Changes' : 'Create Service'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
